import { Component, Inject, OnDestroy, OnInit, Optional } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { interval, Subject } from 'rxjs';
import { takeUntil, takeWhile } from 'rxjs/operators';
import { MfaEnrollmentService } from '../../services/mfa-enrollment.service';
import { ErrorService } from '../../services/error.service';
import { SpinnerService } from '../../services/spinner.service';
import { MfaEnrollmentStartResponse, MfaTotpSetupResponse, MfaEmailSendResponse } from '../../models/mfa';
import * as QRCode from 'qrcode';

export type MfaMethod = 'totp' | 'email';
export type WizardStep = 'loading' | 'select-method' | 'totp-setup' | 'email-send' | 'email-verify' | 'complete' | 'error' | 'mfa-disabled';

@Component({
	selector: 'app-mfa-enrollment-wizard',
	templateUrl: './mfa-enrollment-wizard.component.html',
	styleUrls: ['./mfa-enrollment-wizard.component.scss']
})
export class MfaEnrollmentWizardComponent implements OnInit, OnDestroy {
	public currentStep: WizardStep = 'loading';
	public enrollmentState: MfaEnrollmentStartResponse;
	public totpSetup: MfaTotpSetupResponse | null;
	public totpCode = '';
	public emailAddress = '';
	public emailCode = '';
	public errorMessage = '';
	public isSubmitting = false;
	/**
	 * The resend cooldown, mirroring the challenge dialog. The server enforces the same delay on
	 * v2/MfaEnrollment/Email/Send, so this is the visible half of the rule, not the rule itself — leaving the
	 * button live simply produced a refusal the user could not have predicted.
	 */
	public resendCountdown = 0;
	public canResend = false;
	public enrolledMethod = '';
	public qrCodeDataUrl: string | null = '';
	public mfaDisabledMessage = '';

	private destroy$ = new Subject<void>();

	public gracePeriodDaysRemaining: number | null = null;

	public addMode = false;
	public contactAdminPhone: string | null = null;
	public contactAdminEmail: string | null = null;
	/**
	 * What this user may still enrol in — the tenant policy MINUS anything they already hold. The
	 * method tiles must bind to these and not to enrollmentState.allow*, which is the raw tenant policy:
	 * in addMode that would re-offer a method the user is already enrolled in.
	 */
	public availableTotp = false;
	public availableEmail = false;
	/**
	 * Whether the method-selection step was ever rendered. With a single available method the wizard opens
	 * straight on that method's step, so returning to selection would show a step the flow deliberately
	 * skipped.
	 */
	public selectMethodShown = false;
	private enrolledMethods: string[] = [];

	constructor(
		public dialogRef: MatDialogRef<MfaEnrollmentWizardComponent>,
		private _enrollmentService: MfaEnrollmentService,
		private _errorService: ErrorService,
		private _spinnerService: SpinnerService,
		@Optional() @Inject(MAT_DIALOG_DATA) public data: any
	) {
		if (data) {
			this.addMode = data.addMode || false;
			this.enrolledMethods = data.enrolledMethods || [];
			this.contactAdminPhone = data.contactAdminPhone || null;
			this.contactAdminEmail = data.contactAdminEmail || null;
		}
	}

	ngOnInit(): void {
		this.startEnrollment();
	}

	ngOnDestroy(): void {
		this.qrCodeDataUrl = null;
		this.totpCode = '';
		this.emailCode = '';
		this.totpSetup = null;

		this.destroy$.next();
		this.destroy$.complete();
	}

	startEnrollment(): void {
		this.currentStep = 'loading';
		this._enrollmentService.startEnrollment()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (state) => {
					this.enrollmentState = state;
					this.emailAddress = state.userEmail || '';
					this.computeGracePeriodRemaining(state);
					this.determineInitialStep();
				},
				error: (err) => {
					this._errorService.error(err, 'MFA Enrollment', 'modal');
					this.dialogRef.close({ error: true });
				}
			});
	}

	selectMethod(method: MfaMethod): void {
		if (method === 'totp') {
			this.setupTotp();
		} else if (method === 'email') {
			this.currentStep = 'email-send';
		}
	}

	setupTotp(): void {
		this.isSubmitting = true;
		this.errorMessage = '';
		this._enrollmentService.setupTotp()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (result) => {
					this.totpSetup = result;
					this.isSubmitting = false;
					this.currentStep = 'totp-setup';
					this.generateQrCode(result.provisioningUri);
				},
				error: (err) => {
					this.isSubmitting = false;
					if (this.handleMfaDisabledError(err)) return;
					// F-133: setupTotp() can be entered straight from 'loading' (determineInitialStep on a
					// TOTP-only tenant), where every footer control is hidden. Leaving currentStep on 'loading'
					// after an error strands a disableClose dialog with no controls. Move to a step that carries
					// one: back to the method tiles when they were shown, otherwise the terminal 'error' (Close).
					if (this.currentStep === 'loading') {
						this.currentStep = this.selectMethodShown ? 'select-method' : 'error';
					}
					this.errorMessage = err?.error?.message || 'Failed to set up authenticator.';
				}
			});
	}

	confirmTotp(): void {
		if (this.isSubmitting) return;   // F-141: (keyup.enter) bypasses the disabled button; block re-entry
		if (!this.totpCode || this.totpCode.length !== 6) {
			this.errorMessage = 'Please enter a 6-digit code.';
			return;
		}
		this.isSubmitting = true;
		this.errorMessage = '';
		this._enrollmentService.confirmTotp(this.totpCode)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: () => {
					this.isSubmitting = false;
					this.enrolledMethod = 'AuthenticatorApp';
					this.currentStep = 'complete';
				},
				error: (err) => {
					this.isSubmitting = false;
					if (this.handleMfaDisabledError(err)) return;
					this.totpCode = '';
					// detailedMsg carries the tier's specific reason; fall back to the generic message.
					this.errorMessage = err?.error?.detailedMsg || err?.error?.message || 'Invalid code. Please try again.';
				}
			});
	}

	sendEmailOtp(): void {
		if (this.isSubmitting) return;   // F-141: block a second in-flight send (duplicate OTP emails)
		if (!this.emailAddress) {
			this.errorMessage = 'Please enter an email address.';
			return;
		}
		this.isSubmitting = true;
		this.errorMessage = '';
		this._enrollmentService.sendEmailVerification(this.emailAddress)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: (result: MfaEmailSendResponse) => {
					this.isSubmitting = false;
					this.currentStep = 'email-verify';
					this.startResendCountdown(result?.retryAfterSeconds);
				},
				error: (err) => {
					this.isSubmitting = false;
					if (this.handleMfaDisabledError(err)) return;
					// detailedMsg is where the tier puts its message, so a refused resend reads as
					// "Please wait before requesting a new code." rather than a generic failure.
					this.errorMessage = err?.error?.detailedMsg || err?.error?.message
						|| 'Failed to send verification email.';
				}
			});
	}

	confirmEmail(): void {
		if (this.isSubmitting) return;   // F-141: block re-entry from key auto-repeat / double Enter
		if (!this.emailCode || this.emailCode.length !== 6) {
			this.errorMessage = 'Please enter a 6-digit code.';
			return;
		}
		this.isSubmitting = true;
		this.errorMessage = '';
		this._enrollmentService.confirmEmail(this.emailCode, this.emailAddress)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: () => {
					this.isSubmitting = false;
					this.enrolledMethod = 'EmailOtp';
					this.currentStep = 'complete';
				},
				error: (err) => {
					this.isSubmitting = false;
					if (this.handleMfaDisabledError(err)) return;
					this.emailCode = '';
					// detailedMsg carries the tier's specific reason (e.g. email already in use); fall back to the generic message.
					this.errorMessage = err?.error?.detailedMsg || err?.error?.message || 'Invalid code. Please try again.';
				}
			});
	}

	resendEmailOtp(): void {
		if (!this.canResend || this.isSubmitting) return;
		this.emailCode = '';
		this.errorMessage = '';
		this.sendEmailOtp();
	}

	/**
	 * Counts the cooldown down to zero, then re-enables the button. Seeded from the delay the send response
	 * reports, so an operator changing OtpResendDelaySeconds moves this without a front-end change.
	 */
	private startResendCountdown(seconds: number): void {
		// A tier that reports no delay must not strand the button disabled forever.
		if (!seconds || seconds <= 0) {
			this.resendCountdown = 0;
			this.canResend = true;
			return;
		}

		this.resendCountdown = seconds;
		this.canResend = false;
		interval(1000)
			.pipe(
				takeUntil(this.destroy$),
				takeWhile(() => this.resendCountdown > 0)
			)
			.subscribe(() => {
				this.resendCountdown--;
				this.canResend = this.resendCountdown <= 0;
			});
	}

	skipEnrollment(): void {
		this.isSubmitting = true;
		this._enrollmentService.skipEnrollment()
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: () => {
					this.isSubmitting = false;
					this.dialogRef.close({ skipped: true });
				},
				error: (err) => {
					this.isSubmitting = false;
					if (this.handleMfaDisabledError(err)) return;
					this.errorMessage = err?.error?.message || 'Unable to skip enrollment.';
				}
			});
	}

	completeEnrollment(): void {
		// Carry the verified address so the caller can refresh the cached user's email + verified flag: the
		// Security tab binds to user.inetUser.email / user.emailVerified, which otherwise keep showing the
		// stale pre-enrollment value ("unverified") until the next login re-fetches the profile.
		this.dialogRef.close({
			enrolled: true,
			method: this.enrolledMethod,
			email: this.enrolledMethod === 'EmailOtp' ? this.emailAddress : undefined
		});
	}

	/**
	 * True only on the login-enrollment path, where the user genuinely has enrollment to defer.
	 * Deliberately false in addMode: an already-enrolled user adding a second factor has nothing to
	 * skip, and the backend now rejects a skip from an enrolled user outright, so leaving
	 * the button visible there would surface an error.
	 */
	get canSkipEnrollment(): boolean {
		return !this.addMode
			&& this.enrollmentState?.canSkip === true
			&& this.currentStep !== 'complete'
			&& this.currentStep !== 'loading'
			&& this.currentStep !== 'error'
			&& this.currentStep !== 'mfa-disabled';
	}

	/** addMode counterpart of the skip button — same steps, but it just dismisses the dialog. */
	get canCancelAddMode(): boolean {
		return this.addMode
			&& this.currentStep !== 'complete'
			&& this.currentStep !== 'loading'
			&& this.currentStep !== 'error'
			&& this.currentStep !== 'mfa-disabled';
	}

	/** Dismiss a voluntary add-method flow. Deliberately does NOT call the enrollment-skip endpoint. */
	cancelAddMode(): void {
		this.dialogRef.close();
	}

	/**
	 * Login-path only, and NOT tied to canSkip: once the grace period expires the wizard is deliberately
	 * non-skippable, which previously left the dialog with no control at all — no skip, no
	 * cancel, no logout — so the only escape was clearing cookies or another browser. Signing out grants no
	 * portal access, so this does not weaken EC4; it just stops the session being unleavable.
	 * addMode is excluded because that flow already has Cancel and sits behind the normal portal chrome.
	 */
	get canSignOut(): boolean {
		return !this.addMode
			&& this.currentStep !== 'complete'
			&& this.currentStep !== 'loading'
			// 'error' and 'mfa-disabled' are terminal and already carry their own Close, which ends the
			// flow; a second exit beside it would be two buttons for one job.
			&& this.currentStep !== 'error'
			&& this.currentStep !== 'mfa-disabled';
	}

	/**
	 * The tenant's Contact Admin details, shown only where the user is actually stuck — login path, no skip
	 * available. A blocked user who cannot enroll (no phone, no access to the mailbox) otherwise has no route
	 * to help, even though the tenant configured one.
	 */
	get showContactAdmin(): boolean {
		return !this.addMode
			&& !this.canSkipEnrollment
			&& this.currentStep !== 'complete'
			&& this.currentStep !== 'loading'
			// Deliberately KEPT on 'error': that step tells the user to contact their administrator but
			// gives them no way to, which is exactly the dead end these details exist to close.
			&& this.currentStep !== 'mfa-disabled'
			&& !!(this.contactAdminPhone || this.contactAdminEmail);
	}

	/**
	 * Signals the caller to end the session; the wizard itself does not log out or navigate. It is opened
	 * from a router guard whose navigation is still unresolved, so navigating from here raced that pending
	 * navigation and the guard simply re-opened the wizard. The caller owns teardown
	 * (AuthenticationService.performLogout) and owns settling its own navigation.
	 */
	signOut(): void {
		this.dialogRef.close({ signedOut: true });
	}

	/**
	 * Back is only offered where there is somewhere legitimate to go: returning to method selection needs
	 * that step to have been shown in the first place. email-verify -> email-send is always valid, since
	 * email-send is how email-verify is reached.
	 */
	get canGoBack(): boolean {
		if (this.currentStep === 'email-verify') {
			return true;
		}
		return this.selectMethodShown
			&& (this.currentStep === 'totp-setup' || this.currentStep === 'email-send');
	}

	goBack(): void {
		if (this.currentStep === 'totp-setup' || this.currentStep === 'email-send') {
			if (!this.selectMethodShown) {
				return;
			}
			this.errorMessage = '';
			this.currentStep = 'select-method';
		} else if (this.currentStep === 'email-verify') {
			this.errorMessage = '';
			this.currentStep = 'email-send';
		}
	}

	closeMfaDisabled(): void {
		this.dialogRef.close({ mfaDisabled: true, message: this.mfaDisabledMessage });
	}

	private handleMfaDisabledError(err: any): boolean {
		const message = err?.error?.detailedMsg || err?.error?.message || '';
		if (message.includes('MFA has been disabled')) {
			this.mfaDisabledMessage = message;
			this.currentStep = 'mfa-disabled';
			this.errorMessage = '';
			return true;
		}
		return false;
	}

	get isEnforcedPolicy(): boolean {
		return this.enrollmentState?.customerMfaPolicy === 2;
	}

	private computeGracePeriodRemaining(state: MfaEnrollmentStartResponse): void {
		if (state.gracePeriodActive && state.gracePeriodEndsAt && state.customerMfaPolicy === 2) {
			const endDate = new Date(state.gracePeriodEndsAt);
			const now = new Date();
			const diffMs = endDate.getTime() - now.getTime();
			// F-140: ceil, matching the MT (GetGracePeriodDaysRemaining uses Math.Ceiling) and the notification
			// dialog which renders the server value. Floor showed a 14-day grace as "13 days remaining" seconds
			// after it started, and disagreed with the dialog by a day within the same session.
			this.gracePeriodDaysRemaining = Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
		} else {
			this.gracePeriodDaysRemaining = null;
		}
	}

	private determineInitialStep(): void {
		const state = this.enrollmentState;
		let allowEmail = state.allowEmailOtp;
		let allowTotp = state.allowAuthenticatorApp;

		if (this.enrolledMethods.length > 0) {
			if (this.enrolledMethods.includes('EmailOtp')) allowEmail = false;
			if (this.enrolledMethods.includes('AuthenticatorApp')) allowTotp = false;
		}

		// Kept rather than discarded: the template binds its tiles to these, and Back binds to
		// selectMethodShown. Previously both read the unfiltered policy off enrollmentState.
		this.availableEmail = allowEmail;
		this.availableTotp = allowTotp;

		if (allowEmail && allowTotp) {
			this.selectMethodShown = true;
			this.currentStep = 'select-method';
		} else if (allowTotp) {
			this.setupTotp();
		} else if (allowEmail) {
			this.currentStep = 'email-send';
		} else {
			this.currentStep = 'error';
		}
	}

	private generateQrCode(uri: string): void {
		QRCode.toDataURL(uri, { width: 256, margin: 2 })
			.then((url: string) => {
				this.qrCodeDataUrl = url;
			})
			.catch(() => {
				this.qrCodeDataUrl = '';
			});
	}
}
